import {AccountModel} from '../../models/api/accountModel';
import {CustomFieldModel} from '../../models/api/customFieldModel';

/*
 * Type enum.
 */

export enum AccountAttributeUpdateActionsEnum {
  // Adds
  ADD_DESCRIPTION = 'ADD_DESCRIPTION',
  ADD_CUSTOM_ATTRIBUTE = 'ADD_CUSTOM_ATTRIBUTE',
  // Updates
  UPDATE_NAME = 'UPDATE_NAME',
  UPDATE_DESCRIPTION = 'UPDATE_DESCRIPTION',
  UPDATE_DOMAINS = 'UPDATE_DOMAINS',
  UPDATE_CUSTOM_ATTRIBUTE = 'UPDATE_CUSTOM_ATTRIBUTE',
  // Deletes
  DELETE_DESCRIPTION = 'DELETE_DESCRIPTION',
  DELETE_CUSTOM_ATTRIBUTE = 'DELETE_CUSTOM_ATTRIBUTE',
  DELETE_DOMAINS = 'DELETE_DOMAINS',
}

/*
 * Action Types.
 */

interface AddDescriptionAction {
  type: AccountAttributeUpdateActionsEnum.ADD_DESCRIPTION;
  payload: {
    newDescription: string;
  };
}

interface AddCustomAttributeAction {
  type: AccountAttributeUpdateActionsEnum.ADD_CUSTOM_ATTRIBUTE;
  payload: {
    newAttributeValue: string;
    customField: CustomFieldModel;
  };
}

interface UpdateNameAction {
  type: AccountAttributeUpdateActionsEnum.UPDATE_NAME;
  payload: {
    currentName: string;
    newName: string;
  };
}

interface UpdateDescriptionAction {
  type: AccountAttributeUpdateActionsEnum.UPDATE_DESCRIPTION;
  payload: {
    currentDescription: string;
    newDescription: string;
  };
}

interface UpdateDomainsAction {
  type: AccountAttributeUpdateActionsEnum.UPDATE_DOMAINS;
  payload: {
    currentDomains: ReadonlyArray<string>;
    newDomains: ReadonlyArray<string>;
  };
}

interface UpdateCustomAttributeAction {
  type: AccountAttributeUpdateActionsEnum.UPDATE_CUSTOM_ATTRIBUTE;
  payload: {
    currentAttributeValue: string;
    newAttributeValue: string;
    customField: CustomFieldModel;
  };
}

interface DeleteDescriptionAction {
  type: AccountAttributeUpdateActionsEnum.DELETE_DESCRIPTION;
  payload: {
    currentDescription: string;
  };
}

interface DeleteCustomAttributeAction {
  type: AccountAttributeUpdateActionsEnum.DELETE_CUSTOM_ATTRIBUTE;
  payload: {
    currentAttributeValue: string;
    customField: CustomFieldModel;
  };
}

interface DeleteDomainsAction {
  type: AccountAttributeUpdateActionsEnum.DELETE_DOMAINS;
  payload: {
    currentDomains: ReadonlyArray<string>;
  };
}

/*
 * Action Union Type.
 */

export type AccountAttributeUpdateAction =
  | AddDescriptionAction
  | AddCustomAttributeAction
  | UpdateNameAction
  | UpdateDescriptionAction
  | UpdateDomainsAction
  | UpdateCustomAttributeAction
  | DeleteDescriptionAction
  | DeleteCustomAttributeAction
  | DeleteDomainsAction;

/*
 * Action creators.
 */

export function addDescription(newDescription: string): AddDescriptionAction {
  return {
    type: AccountAttributeUpdateActionsEnum.ADD_DESCRIPTION,
    payload: {
      newDescription,
    },
  };
}

export function addCustomAttribute(
  newAttributeValue: string,
  customField: CustomFieldModel,
): AddCustomAttributeAction {
  return {
    type: AccountAttributeUpdateActionsEnum.ADD_CUSTOM_ATTRIBUTE,
    payload: {
      newAttributeValue,
      customField,
    },
  };
}

export function updateName(currentName: string, newName: string): UpdateNameAction {
  return {
    type: AccountAttributeUpdateActionsEnum.UPDATE_NAME,
    payload: {
      currentName,
      newName,
    },
  };
}

export function updateDescription(
  currentDescription: string,
  newDescription: string,
): UpdateDescriptionAction {
  return {
    type: AccountAttributeUpdateActionsEnum.UPDATE_DESCRIPTION,
    payload: {
      currentDescription,
      newDescription,
    },
  };
}

export function updateDomains(
  currentDomains: ReadonlyArray<string>,
  newDomains: ReadonlyArray<string>,
): UpdateDomainsAction {
  return {
    type: AccountAttributeUpdateActionsEnum.UPDATE_DOMAINS,
    payload: {
      currentDomains,
      newDomains,
    },
  };
}

export function updateCustomAttribute(
  currentAttributeValue: string,
  newAttributeValue: string,
  customField: CustomFieldModel,
): UpdateCustomAttributeAction {
  return {
    type: AccountAttributeUpdateActionsEnum.UPDATE_CUSTOM_ATTRIBUTE,
    payload: {
      currentAttributeValue,
      newAttributeValue,
      customField,
    },
  };
}

export function deleteDescription(currentDescription: string): DeleteDescriptionAction {
  return {
    type: AccountAttributeUpdateActionsEnum.DELETE_DESCRIPTION,
    payload: {
      currentDescription,
    },
  };
}

export function deleteCustomAttribute(
  currentAttributeValue: string,
  customField: CustomFieldModel,
): DeleteCustomAttributeAction {
  return {
    type: AccountAttributeUpdateActionsEnum.DELETE_CUSTOM_ATTRIBUTE,
    payload: {
      currentAttributeValue,
      customField,
    },
  };
}

export function deleteDomains(currentDomains: ReadonlyArray<string>): DeleteDomainsAction {
  return {
    type: AccountAttributeUpdateActionsEnum.DELETE_DOMAINS,
    payload: {
      currentDomains,
    },
  };
}

/*
 * Reducer.
 */

export function accountAttributeUpdateReducer(
  existingAccount: AccountModel,
  action: AccountAttributeUpdateAction,
) {
  switch (action.type) {
    // Adds
    case AccountAttributeUpdateActionsEnum.ADD_DESCRIPTION:
      return {
        ...existingAccount,
        description: action.payload.newDescription,
      };
    case AccountAttributeUpdateActionsEnum.ADD_CUSTOM_ATTRIBUTE:
      return {
        ...existingAccount,
        customFieldAttributes: [
          ...(existingAccount.customFieldAttributes ?? []),
          {id: action.payload.customField.id, value: action.payload.newAttributeValue},
        ],
      };
    // Updates
    case AccountAttributeUpdateActionsEnum.UPDATE_NAME:
      return {
        ...existingAccount,
        name: action.payload.newName,
      };
    case AccountAttributeUpdateActionsEnum.UPDATE_DESCRIPTION:
      return {
        ...existingAccount,
        description: action.payload.newDescription,
      };
    case AccountAttributeUpdateActionsEnum.UPDATE_DOMAINS:
      return {
        ...existingAccount,
        domains: action.payload.newDomains,
      };
    case AccountAttributeUpdateActionsEnum.UPDATE_CUSTOM_ATTRIBUTE:
      return {
        ...existingAccount,
        customFieldAttributes: existingAccount.customFieldAttributes?.map((customAttribute) =>
          customAttribute.id === action.payload.customField.id
            ? {...customAttribute, value: action.payload.newAttributeValue}
            : customAttribute,
        ),
      };

    // Deletes
    case AccountAttributeUpdateActionsEnum.DELETE_DESCRIPTION:
      return {
        ...existingAccount,
        description: undefined,
      };
    case AccountAttributeUpdateActionsEnum.DELETE_CUSTOM_ATTRIBUTE:
      return {
        ...existingAccount,
        customFieldAttributes: existingAccount.customFieldAttributes?.filter(
          (customAttribute) => customAttribute.id !== action.payload.customField.id,
        ),
      };
    case AccountAttributeUpdateActionsEnum.DELETE_DOMAINS:
      return {
        ...existingAccount,
        domains: [],
      };
    default: {
      return existingAccount;
    }
  }
}

/*
 * Inverse actions for undos.
 */

export function computeInverseAccountAttributeUpdateAction(action: AccountAttributeUpdateAction) {
  switch (action.type) {
    // Adds
    case AccountAttributeUpdateActionsEnum.ADD_DESCRIPTION:
      return deleteDescription(action.payload.newDescription);
    case AccountAttributeUpdateActionsEnum.ADD_CUSTOM_ATTRIBUTE:
      return deleteCustomAttribute(action.payload.newAttributeValue, action.payload.customField);

    // Updates
    case AccountAttributeUpdateActionsEnum.UPDATE_NAME:
      return updateName(action.payload.newName, action.payload.currentName);
    case AccountAttributeUpdateActionsEnum.UPDATE_DESCRIPTION:
      return updateDescription(action.payload.newDescription, action.payload.currentDescription);
    case AccountAttributeUpdateActionsEnum.UPDATE_CUSTOM_ATTRIBUTE:
      return updateCustomAttribute(
        action.payload.newAttributeValue,
        action.payload.currentAttributeValue,
        action.payload.customField,
      );

    // Deletes
    case AccountAttributeUpdateActionsEnum.DELETE_DESCRIPTION:
      return addDescription(action.payload.currentDescription);
    case AccountAttributeUpdateActionsEnum.DELETE_CUSTOM_ATTRIBUTE:
      return addCustomAttribute(action.payload.currentAttributeValue, action.payload.customField);

    default:
      return action;
  }
}
